import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';

import FadeIn from '../../../components/animations/FadeIn';
import AddressAutocompleteInput from '../../../components/AddressAutocompleteInput';
import { buildPortalUrl } from '../../../utils/cn';

// Desktop hero — rebuild against the reference markup + CSS Mike supplied
// 2026-05-20. Reference uses class-based hooks (.hero, .hero-bg,
// .hero-eyebrow, .address-card, .hero-quick-stat, etc.) defined in
// src/styles/tailwind.css under @layer components. Tailwind utilities are
// only used where they don't conflict.
//
// Brand-blue note: the reference uses var(--blue) which is bound to the
// codebase brand primary #07B0E9 (NOT the reference's #1ba3f0 placeholder).
//
// Reveal animations use the shared FadeIn component
// (src/components/animations/FadeIn.jsx). Staggered delays 80/160/240/320/400/480ms
// match the original reference's `data-delay="1..6"` convention (80ms × N).

// TYPING-LAG FIX (2026-07-18, CEO-reported sticky hero input): the per-keystroke
// state used to live on HeroSection itself, so EVERY keystroke re-rendered the
// entire hero — six FadeIn motion wrappers plus the infinite "$0 down" glow
// tween, whose `animate`/`transition` props were recreated as fresh objects each
// render, forcing framer-motion to re-reconcile a paint-heavy 8→28px
// drop-shadow tween on each key press. Two changes:
//   1. The glow tween objects are hoisted to module constants (stable identity).
//   2. All typing state lives in HeroAddressForm below, so keystrokes re-render
//      only the small input+button subtree — the animated hero never re-renders.
// (Network was ruled out: the Mapbox autocomplete is debounced 300ms — a live
// capture showed a single request for 22 characters of continuous typing.)
const ZERO_GLOW_ANIMATE = {
  filter: [
    'drop-shadow(0 0 8px rgba(7,176,233,0.3))',
    'drop-shadow(0 0 28px rgba(7,176,233,0.9))',
    'drop-shadow(0 0 8px rgba(7,176,233,0.3))',
  ],
};
const ZERO_GLOW_TRANSITION = {
  duration: 4,
  repeat: Infinity,
  ease: 'easeInOut',
  times: [0, 0.5, 1],
};

// Owns all per-keystroke state — see TYPING-LAG FIX note above.
const HeroAddressForm = () => {
  const [address, setAddress] = useState('');
  const [selectedAddressData, setSelectedAddressData] = useState(null);
  const [addressError, setAddressError] = useState('');

  const redirectToPortal = (data) => {
    const fullAddress = data?.fullAddress || data?.address || '';
    if (!fullAddress) return;
    window.location.href = buildPortalUrl(fullAddress);
  };

  // Raw typing — clear the previous selection so the button-click path
  // re-validates (user can't type junk after picking from the dropdown).
  const handleAddressChange = (value) => {
    setAddress(value);
    if (selectedAddressData) setSelectedAddressData(null);
    if (addressError) setAddressError('');
  };

  // Selecting from the dropdown is an immediate submit — matches the
  // pre-rebuild AddressSearchBar behavior.
  const handleAddressSelect = (data) => {
    setSelectedAddressData(data);
    setAddress(data?.fullAddress || '');
    setAddressError('');
    redirectToPortal(data);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedAddressData?.selected) {
      // The suggestions dropdown only appears once 3+ characters are typed
      // (AddressAutocompleteInput's Mapbox threshold) — so with an empty or
      // barely-started input, "select from the dropdown" points at UI the
      // visitor has never seen. Ask for the address first; only mention the
      // dropdown once suggestions could actually have been shown.
      setAddressError(
        address.trim().length < 3
          ? 'Please enter your home address'
          : 'Please select an address from the dropdown'
      );
      return;
    }
    redirectToPortal(selectedAddressData);
  };

  return (
    <>
      <FadeIn as="form" className="address-form" delay={0.32} id="hero-form" onSubmit={handleSubmit}>
        <div className="address-card">
          <div className="address-input-wrap">
            <svg
              className="pin-icon"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {/* Mapbox-backed autocomplete — same primitive the mobile hero
                uses. showIcon={false} keeps our positioned .pin-icon SVG;
                flex-1 wrapper lets the input fill the card's remaining
                width and anchors the dropdown beneath the input. */}
            <AddressAutocompleteInput
              value={address}
              onChange={handleAddressChange}
              onSelect={handleAddressSelect}
              placeholder="Enter your home address"
              showIcon={false}
              className="flex-1"
              inputClassName="address-input"
            />
          </div>
          <motion.button
            type="submit"
            className="address-submit"
            whileHover={{ y: -2, boxShadow: '0 12px 32px rgba(7, 176, 233, 0.6)' }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <span>Get my roof price</span>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </motion.button>
        </div>
        {addressError && (
          <p className="address-error" role="alert">{addressError}</p>
        )}
      </FadeIn>
    </>
  );
};

const HeroSection = () => {
  const sectionRef = useRef(null);

  return (
    <section ref={sectionRef} className="hero">
      <div className="hero-bg" aria-hidden="true">
        <div className="hero-overlay" aria-hidden="true" />
      </div>

      <div className="container hero-content">
        {/* Status pill ("OpenbooQ · Live in Dayton & Cincinnati") was
            removed 2026-05-26 to reclaim vertical space so the new
            $0 Promise treatment fits within the hero without being
            cut off at the fold. The .hero-eyebrow / .brand-prefix
            CSS in tailwind.css is now unreferenced but left in
            place (pulse-dot, which the eyebrow used, is still used
            by StickyCallTab). */}

        <FadeIn as="h1" delay={0.08}>
          Buy Your Roof Online.<br />
          Directly from <span className="em">OpenbooQ</span>
        </FadeIn>

        <FadeIn as="p" className="hero-sub" delay={0.16}>
          {/* Continuous cyan breathing glow on "$0 down." — pulls eye to the
              biggest objection-killer in the subhead. The .zero class still
              provides the underline highlight via ::after. */}
          {/* Constant low-level cyan glow on "$0 down" (8px @ 0.3 opacity)
              that pulses up to a strong 28px @ 0.9 peak every 4 seconds.
              The non-zero baseline means the text reads as "highlighted"
              even between peaks — pulls eye to the biggest objection-killer
              in the subhead without disappearing into the busy hero. */}
          <motion.span
            className="zero"
            animate={ZERO_GLOW_ANIMATE}
            transition={ZERO_GLOW_TRANSITION}
          >
            $0 down.
          </motion.span>{' '}
          Pay thousands less.
        </FadeIn>

        {/* 4-step horizontal funnel preview */}
        <FadeIn className="hero-steps" delay={0.24}>
          <div className="hero-step active">
            <div className="hero-step-num">1</div>
            <div className="hero-step-label">Address</div>
          </div>
          <div className="hero-step-line" />
          <div className="hero-step">
            <div className="hero-step-num">2</div>
            <div className="hero-step-label">Design</div>
          </div>
          <div className="hero-step-line" />
          <div className="hero-step">
            <div className="hero-step-num">3</div>
            <div className="hero-step-label">Customize</div>
          </div>
          <div className="hero-step-line" />
          <div className="hero-step">
            <div className="hero-step-num">4</div>
            <div className="hero-step-label">Quote</div>
          </div>
        </FadeIn>

        <HeroAddressForm />

        <FadeIn className="hero-trust-strip" delay={0.4}>
          <span><strong>No high-pressure sales</strong>, ever</span>
          <span className="sep">·</span>
          <span>
            <span className="stars">★★★★★</span> <strong>5.0</strong> on Google · 32 verified reviews
          </span>
        </FadeIn>

        {/* Scaled-down "$0 Promise" treatment — replaces the prior three
            trust-stat badges (0% markup / 20% flat fee / 30-50% less).
            Pulls the eyebrow + glowing-gradient $0 + headline from the
            standalone ZeroSection below, sized as a hero accent (~1/3
            scale). No background of its own — sits on the existing
            hero overlay, which is 70% navy in this band so the cyan
            glow stays legible over the craftsman-home photo. The hover
            glow intensification matches the standalone ZeroSection's
            $0 behavior; touch devices skip whileHover entirely. */}
        <FadeIn className="hero-zero-promise" delay={0.48}>
          <div className="hero-zero-eyebrow">The OpenbooQ Promise</div>
          <motion.div
            className="hero-zero-graphic"
            whileHover={{
              filter: 'drop-shadow(0 12px 40px rgba(7, 176, 233, 0.8))',
              transition: { duration: 0.4, ease: 'easeOut' },
            }}
          >
            $0
          </motion.div>
          <p className="hero-zero-headline">You don't pay until your roof is on.</p>
        </FadeIn>
      </div>

      <div className="scroll-cue" aria-hidden="true">
        Scroll
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <line x1="12" y1="5" x2="12" y2="19" />
          <polyline points="19 12 12 19 5 12" />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
